import { Check, X, AlertTriangle, Loader2, Radio, FlaskConical } from 'lucide-react'
import { STAGES } from '../data'
import type { StageId, StageStatus } from '../types'

function statusColors(status: StageStatus) {
  switch (status) {
    case 'pass': return { bg: '#f0fdf4', border: '#86efac', text: '#15803d' }
    case 'fail': return { bg: '#fef2f2', border: '#b91c1c', text: '#b91c1c' }
    case 'refer': return { bg: '#fff7ed', border: '#c2410c', text: '#c2410c' }
    case 'running': return { bg: '#fffbeb', border: '#b45309', text: '#b45309' }
    case 'skipped': return { bg: '#f8fafc', border: '#e5e7eb', text: '#64748b' }
    default: return { bg: '#ffffff', border: '#e5e7eb', text: '#334155' }
  }
}

function StatusIcon({ status }: { status: StageStatus }) {
  if (status === 'pass') return <Check size={12} strokeWidth={2.5} />
  if (status === 'fail') return <X size={12} strokeWidth={2.5} />
  if (status === 'refer') return <AlertTriangle size={12} strokeWidth={2.5} />
  if (status === 'running') return <Loader2 size={12} className="animate-spin" />
  return null
}

interface StageRailProps {
  statuses: Record<StageId, StageStatus>
  activeStage: StageId
  onSelect: (id: StageId) => void
  /** Restrict which stages render (e.g. hide products an existing customer doesn't have provisioned). Omit to show every stage. */
  visibleStageIds?: StageId[]
}

export function StageRail({ statuses, activeStage, onSelect, visibleStageIds }: StageRailProps) {
  const stages = visibleStageIds ? STAGES.filter((s) => visibleStageIds.includes(s.id)) : STAGES
  // Once the active stage has actually settled (pass/fail/refer/skipped — not still
  // pending/running), point at whichever stage comes next so the rail always makes
  // clear where to go, independent of which stage happens to be selected right now.
  const activeIndex = stages.findIndex((s) => s.id === activeStage)
  const activeStatus = statuses[activeStage]
  const activeIsDone = activeStatus !== 'pending' && activeStatus !== 'running'
  const nextUpId = activeIsDone && activeIndex !== -1 && activeIndex < stages.length - 1
    ? stages[activeIndex + 1].id
    : null

  return (
    <div className="flex items-stretch gap-2 overflow-x-auto pb-1">
      {stages.map((stage, i) => {
        const status = statuses[stage.id]
        const colors = statusColors(status)
        const isActive = activeStage === stage.id
        const isNextUp = stage.id === nextUpId
        return (
          <div key={stage.id} className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => onSelect(stage.id)}
              className="flex flex-col gap-1.5 px-3.5 py-2.5 rounded-[12px] text-left transition-all min-w-[168px]"
              style={{
                background: colors.bg,
                // Green is reserved for a stage that has actually passed — "you are here"
                // uses indigo instead, so a pending/in-progress current stage never reads
                // as already complete. A dashed indigo border points at what's next once
                // the active stage has settled.
                border: isActive
                  ? '1px solid #4f46e5'
                  : isNextUp
                    ? '1px dashed #818cf8'
                    : `1px solid ${colors.border}`,
                boxShadow: isActive ? '0 0 0 2px rgba(99,102,241,.35)' : 'none',
              }}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-[12px] font-semibold" style={{ color: colors.text }}>{stage.label}</span>
                <StatusIcon status={status} />
              </div>
              <p className="text-[9px] text-[#64748b] leading-snug">{stage.tagline}</p>
              <div className="flex items-center gap-1.5 flex-wrap">
                <span
                  className="inline-flex items-center gap-1 w-fit text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                  style={stage.live
                    ? { background: '#dcfce7', color: '#15803d' }
                    : { background: '#ede9fe', color: '#7c3aed' }
                  }
                >
                  {stage.live ? <><Radio size={8} /> Live</> : <><FlaskConical size={8} /> Simulated</>}
                </span>
                {isNextUp && (
                  <span
                    className="inline-flex items-center gap-1 w-fit text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                    style={{ background: 'rgba(99,102,241,.12)', color: '#4f46e5' }}
                  >
                    Up next
                  </span>
                )}
              </div>
            </button>
            {i < stages.length - 1 && <span className="text-[#64748b] text-sm font-bold shrink-0">→</span>}
          </div>
        )
      })}
    </div>
  )
}
